import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, CURRENCIES } from '../lib/utils';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import { 
  Plus, 
  Repeat, 
  MoreHorizontal, 
  Pause, 
  Play, 
  Trash2, 
  Edit,
  Loader2,
  Calendar,
  Clock
} from 'lucide-react';

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

const RecurringInvoices = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    auto_send: false,
    currency: 'USD',
    tax_rate: 0,
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recurringRes, clientsRes, brandingRes] = await Promise.all([
        api.get('/recurring-invoices'),
        api.get('/clients'),
        api.get('/branding')
      ]);
      setRecurringInvoices(recurringRes.data);
      setClients(clientsRes.data);
      
      // Set default currency from branding
      const defaultCurrency = brandingRes.data?.branding?.default_currency || 'USD';
      setFormData(prev => ({ ...prev, currency: defaultCurrency }));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      frequency: 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      auto_send: false,
      currency: 'USD',
      tax_rate: 0,
      notes: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index 
          ? { ...item, [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value }
          : item
      )
    }));
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = subtotal * (formData.tax_rate / 100);
    return subtotal + tax;
  };

  const handleCreate = async () => {
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (formData.items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Please fill in all item details');
      return;
    }

    setSaving(true);
    try {
      await api.post('/recurring-invoices', {
        client_id: formData.client_id,
        items: formData.items,
        frequency: formData.frequency,
        start_date: formData.start_date,
        auto_send: formData.auto_send,
        currency: formData.currency,
        tax_rate: formData.tax_rate,
        notes: formData.notes || null
      });
      toast.success('Recurring invoice created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create recurring invoice'));
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async (recurringId) => {
    try {
      await api.post(`/recurring-invoices/${recurringId}/pause`);
      toast.success('Recurring invoice paused');
      fetchData();
    } catch (error) {
      toast.error('Failed to pause recurring invoice');
    }
  };

  const handleResume = async (recurringId) => {
    try {
      await api.post(`/recurring-invoices/${recurringId}/resume`);
      toast.success('Recurring invoice resumed');
      fetchData();
    } catch (error) {
      toast.error('Failed to resume recurring invoice');
    }
  };

  const handleDelete = async (recurringId) => {
    if (!window.confirm('Are you sure you want to delete this recurring invoice?')) return;
    try {
      await api.delete(`/recurring-invoices/${recurringId}`);
      toast.success('Recurring invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete recurring invoice');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
      case 'paused':
        return <Badge className="bg-amber-100 text-amber-700">Paused</Badge>;
      case 'cancelled':
        return <Badge className="bg-rose-100 text-rose-700">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-12 space-y-8" data-testid="recurring-invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight flex items-center gap-3">
              <Repeat className="w-8 h-8 text-primary" />
              Recurring Invoices
            </h1>
            <p className="text-muted-foreground mt-1">
              Automate your invoicing with recurring templates
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-recurring-btn">
            <Plus className="w-4 h-4" />
            New Recurring Invoice
          </Button>
        </div>

        {/* Recurring Invoices List */}
        <Card>
          <CardHeader>
            <CardTitle>All Recurring Invoices</CardTitle>
            <CardDescription>
              {recurringInvoices.length} recurring invoice{recurringInvoices.length !== 1 ? 's' : ''} configured
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recurringInvoices.length === 0 ? (
              <div className="text-center py-12">
                <Repeat className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium text-lg mb-2">No recurring invoices yet</h3>
                <p className="text-muted-foreground mb-4">
                  Set up recurring invoices for retainer clients or subscription services
                </p>
                <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create Your First Recurring Invoice
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Next Invoice</TableHead>
                    <TableHead>Auto-Send</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recurringInvoices.map((rec) => {
                    const subtotal = rec.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
                    const total = subtotal + (subtotal * (rec.tax_rate || 0) / 100);
                    
                    return (
                      <TableRow key={rec.recurring_id} data-testid={`recurring-row-${rec.recurring_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{rec.client_name}</p>
                            <p className="text-sm text-muted-foreground">{rec.client_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">
                          {formatCurrency(total, rec.currency)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="capitalize">{rec.frequency}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            {formatDate(rec.next_invoice_date)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {rec.auto_send ? (
                            <Badge variant="outline" className="text-emerald-600">Yes</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">No (Draft)</Badge>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(rec.status)}</TableCell>
                        <TableCell className="text-center">{rec.invoices_generated}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`recurring-menu-${rec.recurring_id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {rec.status === 'active' ? (
                                <DropdownMenuItem onClick={() => handlePause(rec.recurring_id)}>
                                  <Pause className="w-4 h-4 mr-2" />
                                  Pause
                                </DropdownMenuItem>
                              ) : rec.status === 'paused' ? (
                                <DropdownMenuItem onClick={() => handleResume(rec.recurring_id)}>
                                  <Play className="w-4 h-4 mr-2" />
                                  Resume
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuItem 
                                onClick={() => handleDelete(rec.recurring_id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Recurring Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Recurring Invoice</DialogTitle>
            <DialogDescription>
              Set up an invoice that will be automatically generated on a schedule
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Client Selection */}
            <div>
              <Label>Client *</Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="recurring-client-select">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.name} ({client.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Schedule Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frequency *</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData(prev => ({ ...prev, frequency: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="recurring-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((freq) => (
                      <SelectItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1.5"
                  data-testid="recurring-start-date"
                />
              </div>
            </div>

            {/* Auto-send Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Auto-send invoices</p>
                <p className="text-sm text-muted-foreground">
                  {formData.auto_send 
                    ? "Invoices will be automatically sent to the client" 
                    : "Invoices will be created as drafts for you to review"}
                </p>
              </div>
              <Switch
                checked={formData.auto_send}
                onCheckedChange={(v) => setFormData(prev => ({ ...prev, auto_send: v }))}
                data-testid="recurring-auto-send-switch"
              />
            </div>

            {/* Currency & Tax */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="recurring-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCIES).map(([code, info]) => (
                      <SelectItem key={code} value={code}>
                        {info.symbol} {code} - {info.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                  className="mt-1.5"
                  data-testid="recurring-tax-rate"
                />
              </div>
            </div>

            {/* Line Items */}
            <div>
              <Label className="mb-3 block">Line Items *</Label>
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        data-testid={`recurring-item-desc-${index}`}
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        min="1"
                        step="0.01"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        data-testid={`recurring-item-qty-${index}`}
                      />
                    </div>
                    <div className="w-28">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        data-testid={`recurring-item-price-${index}`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(index)}
                      disabled={formData.items.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Item
                </Button>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notes to include on each generated invoice..."
                className="mt-1.5"
                data-testid="recurring-notes"
              />
            </div>

            {/* Total Preview */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">Amount per invoice:</span>
                <span className="text-2xl font-bold font-mono">
                  {formatCurrency(calculateTotal(), formData.currency)}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-recurring-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Recurring Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default RecurringInvoices;
